var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DataContextProvider, RefreshScope, useAuth, useDataTable } from 'react-admin-base';
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from 'react-router-dom';
import { Alert, Button, Card, CardFooter, CardHeader, Col, Input, Row, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import BootstrapPagination from "./BootstrapPagination";
const DataTableContext = React.createContext(null);
export function useDataTableContext() {
    return useContext(DataTableContext);
}
export function Actions({ edit, del, rowSpan, children }) {
    const [api] = useAuth();
    const [, setParams] = useContext(DataTableContext);
    const intl = useIntl();
    const [loading, setLoading] = useState(false);
    const delFnc = useCallback(function (e) {
        return __awaiter(this, void 0, void 0, function* () {
            e && e.preventDefault();
            const val = yield Swal.fire({
                title: intl.formatMessage({ id: 'ACTIONS.DELETE.TITLE' }),
                text: intl.formatMessage({ id: 'ACTIONS.DELETE.TEXT' }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: intl.formatMessage({ id: 'ACTIONS.DELETE.CONFIRM' })
            });
            if (val.value) {
                setLoading(true);
                try {
                    yield api.tokenized.delete(del);
                    setParams({}, true);
                }
                finally {
                    setLoading(false);
                }
            }
        });
    }, [api, del, setParams, intl, setLoading]);
    return React.createElement("td", { className: "min", rowSpan: rowSpan },
        edit && React.createElement(Link, { to: edit },
            React.createElement(Button, { outline: true, color: "primary" },
                React.createElement("i", { className: "fa fa-pencil-alt" }))),
        edit && del && ' ',
        del && React.createElement(Button, { color: "danger", outline: true, onClick: delFnc, disabled: loading },
            React.createElement("i", { className: loading ? "fa fa-spin fa-spinner" : "fa fa-trash" })),
        children);
}
export function IdColumn() {
    return React.createElement(Column, { className: "min text-center", sort: "id" },
        React.createElement("i", { className: "fas fa-hashtag" }));
}
export function ActionsColumn() {
    return React.createElement(Column, { className: "min text-center" },
        React.createElement("i", { className: "fas fa-water" }));
}
export function Column(props) {
    const [params, setParams] = useContext(DataTableContext);
    const { sort } = props;
    return React.createElement("th", Object.assign({}, props, { style: props.sort && { cursor: 'pointer' }, onClick: props.sort && (() => setParams(params => (Object.assign(Object.assign({}, params), { sort: sort, desc: params.sort === sort ? !params.desc : true })))) }),
        props.children,
        " ",
        sort && params.sort && params.sort === sort ? params.desc ?
            React.createElement("i", { className: "fa fa-sort-down" }) :
            React.createElement("i", { className: "fa fa-sort-up" }) : '');
}
export default function BootstrapTable({ url, bordered, noStrip, defaultParams, add, children, innerRef, body }) {
    var state = useState(Object.assign({ sort: 'id' }, defaultParams));
    const [params, setParams] = state;
    const [page, lastPage, setPage, data, itemPerPage, setItemPerPage, update] = useDataTable(url, params, body);
    const intl = useIntl();
    const [api] = useAuth();
    var ref = useRef(defaultParams);
    useEffect(function () {
        if (ref.current !== defaultParams) {
            ref.current = defaultParams;
            setParams(params => (Object.assign(Object.assign({}, params), defaultParams)));
        }
    }, [defaultParams, ref, setParams]);
    useEffect(function () {
        if (innerRef) {
            innerRef.current = setParams;
            return function () {
                innerRef.current = null;
            };
        }
    }, [setParams, innerRef]);
    const fetchData = useCallback(function (extraParams) {
        return __awaiter(this, void 0, void 0, function* () {
            if (body) {
                const data = yield api.tokenized.post(url, body, { params: Object.assign(Object.assign({}, params), (extraParams || {})) });
                return data.data;
            }
            const data = yield api.tokenized.get(url, { params: Object.assign(Object.assign({}, params), (extraParams || {})) });
            return data.data;
        });
    }, [api, url, params, body]);
    return React.createElement(Card, null,
        React.createElement(DataTableContext.Provider, { value: state },
            React.createElement(DataContextProvider, { value: fetchData },
                React.createElement(RefreshScope, { update: update },
                    React.createElement(CardHeader, null,
                        React.createElement(Row, null,
                            add && React.createElement(Col, { xs: "12", md: "2" },
                                React.createElement(Link, { to: add, className: "btn btn-primary font-xl d-block" },
                                    React.createElement("i", { className: "fa fa-plus" }))),
                            React.createElement(Col, { md: "2" },
                                React.createElement(Input, { type: "select", value: itemPerPage.toString(), onChange: a => setItemPerPage(+a.currentTarget.value) },
                                    React.createElement("option", { value: "1" }, "1"),
                                    React.createElement("option", { value: "20" }, "20"),
                                    React.createElement("option", { value: "50" }, "50"),
                                    React.createElement("option", { value: "100" }, "100"),
                                    React.createElement("option", { value: "150" }, "150"),
                                    React.createElement("option", { value: "200" }, "200"),
                                    React.createElement("option", { value: "-1" }, intl.formatMessage({ id: "ALL" })))),
                            children[2],
                            React.createElement(Col, { md: "3", className: "ms-auto" },
                                React.createElement(Input, { placeholder: intl.formatMessage({ id: "SEARCH" }), type: "text", value: params.query || '', onChange: e => setParams(Object.assign(Object.assign({}, params), { query: e.currentTarget.value })) })))),
                    data === null ? React.createElement(Alert, { className: "text-center mb-0 mx-3 ", color: "warning" },
                        React.createElement("i", { className: "fas fa-spinner fa-spin" })) : !data.length ? React.createElement(Alert, { className: "text-center mx-3", color: "danger" },
                        React.createElement("i", { className: "far fa-times-circle" }),
                        " ",
                        React.createElement(FormattedMessage, { id: "NO_DATA_IS_AVAILABLE" })) : React.createElement(Table, { hover: true, bordered: bordered, striped: !noStrip, responsive: true, size: "md", className: "mb-0 dataTable" },
                        children[0],
                        React.createElement("tbody", null, data && data.map(children[1].props.children))),
                    lastPage > 1 && React.createElement(CardFooter, null,
                        React.createElement("nav", null,
                            React.createElement(BootstrapPagination, { currentPage: page, pageCount: lastPage, onPageChange: index => setPage(index) })))))));
}
